import { autoinject, transient, computedFrom } from "aurelia-framework";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { VoyageDataSubmissionService } from "../../../../../../services/vetting/voyage-data-submission.service";
import { RawVoyageDataValidationErrorModel, VoyageDataSubmissionModel } from "../../../../../../models/vetting/vetting.models";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import * as moment from "moment";
import { CultureInfo } from "../../../../../../models/internationalization/culture-info.models";
import { CultureInfoService } from "../../../../../../services/internationalization/culture-info.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { FinancialYearTypeEnum } from "../../../../../../models/carbon-accounting/carbon-accounting.models";
import { Claims } from "../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Guid } from "../../../../../../utilities/guid";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject
@transient()
export class AddVoyageDataSubmissionDialog {

    public attachments: File[] = [];
    public validationErrors: RawVoyageDataValidationErrorModel[] = [];
    public yearAsDate: Date;
    public financialYearTypeId: number;
    public financialYearReportingPeriodId: number;
    public reportingPeriodTypeId: number;
    public existingVoyageDataSubmission: { voyageDataSubmissionId:number, reportingPeriodName: string } [];
    public culture: CultureInfo;
    public fromYear: number;
    public toYear: number;
    public selectedFinancialYear: number;
    public selectedFinancialYearReportingPeriod: LookupValue;
    public showIncludesBallastLeg: boolean = false;
    public ballastLegsIncluded: boolean = false;
    public customerId?: number;
    public selectedBusinessUnitId?: number;
    public isRightShipUser: boolean;
    public isDataLoaded: boolean = false;

    @computedFrom("existingVoyageDataSubmission")
    public get voyageDataSubmissionAlreadyExists(): boolean {
        if (!this.existingVoyageDataSubmission || this.existingVoyageDataSubmission.length == 0)
            return false;
        
        return true;
    }

    public get cultures(): CultureInfo[] {
        return this.cultureInfoService.cultures;
    }

    constructor(private voyageDataSubmissionService: VoyageDataSubmissionService,
        private notificationService: NotificationClientService, private router: Router,
        private dialogClientService: DialogClientService, private validationsClientService: ValidationsClientService,
        private cultureInfoService: CultureInfoService, private userService: UserClientService, 
        private userClaims: UserClaimsService) {
            this.showIncludesBallastLeg = this.userClaims.hasPermission(Claims.ShowIncludesBallastLeg);
    }

    async activate(model: any) {
        this.isDataLoaded = true;

        if (model) {
            this.fromYear = model.fromYear;
            this.toYear = moment().year();
            this.customerId = model.customerId;
            this.isRightShipUser = model.isRightShipUser;
            if (model.ballastLegsIncluded)
                this.ballastLegsIncluded = model.ballastLegsIncluded;
        }
        
        const response = await this.voyageDataSubmissionService.currentCaReportingPeriod(this.customerId);
        const date = new Date();
        if (response) {
            this.reportingPeriodTypeId = response.reportingPeriodTypeId;
            this.financialYearReportingPeriodId = response.financialYearReportingPeriodId;
            this.financialYearTypeId = response.financialYearTypeId;

            if (!this.financialYearTypeId) {
                this.notificationService.show("Error loading Voyage Data Submission", "", NotificationType.Error);
                this.isDataLoaded = false;
            }

            if (response.year)
                date.setFullYear(response.year);
        }
        this.yearAsDate = date;
        this.checkForExistingVoyageDataSubmission();
        this.culture = this.userService.getCurrentUserCulture();
    }

    checkForExistingVoyageDataSubmission() {
        if (!(this.yearAsDate instanceof Date) || !this.financialYearReportingPeriodId)
            return;
        const year = moment(this.yearAsDate).year();
        this.voyageDataSubmissionService.getVoyageDataSubmissionIdIfWasSubmittedOrCompleted(year, this.financialYearReportingPeriodId, this.customerId).then(response => {
            this.existingVoyageDataSubmission = response.voyageReports;
        });
    }

    yearChanged($event) {
        if (!$event.detail)
            return;

        this.checkForExistingVoyageDataSubmission();
    }

    async reportingPeriodSelectedChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.item.id === $event.detail.oldValue.id))
            return;

        this.selectedFinancialYearReportingPeriod = $event.detail.item;
        this.convertFinancialYearToDate();
        this.checkForExistingVoyageDataSubmission();
    }

    reportingPeriodYearSelectedChanged($event) {
        if (!$event.detail || !$event.detail.item)
            return;
        
        this.selectedFinancialYear = $event.detail.item.additionalData.financialYear;
        this.convertFinancialYearToDate();
        this.checkForExistingVoyageDataSubmission();
    }

    close() {
        this.dialogClientService.hide();
    }
 
    get invalidYear(): boolean { return !this.yearAsDate && this.trySubmit; }
 
    @computedFrom(Reflection.getPropertyPath<AddVoyageDataSubmissionDialog>(z => z.financialYearReportingPeriodId),
        Reflection.getPropertyPath<AddVoyageDataSubmissionDialog>(z => z.trySubmit))
    get invalidReportingPeriod(): boolean { return !this.financialYearReportingPeriodId && this.trySubmit; }

    @computedFrom(Reflection.getPropertyPath<AddVoyageDataSubmissionDialog>(z => z.attachments),
    Reflection.getPropertyPath<AddVoyageDataSubmissionDialog>(z => z.attachments.length),
        Reflection.getPropertyPath<AddVoyageDataSubmissionDialog>(z => z.trySubmit),
        Reflection.getPropertyPath<AddVoyageDataSubmissionDialog>(z => z.validationErrors))
    get attachmentsRequired(): boolean { 
        const required = this.attachments.length <= 0 && this.trySubmit;
        if(required && this.validationErrors?.length > 0) this.validationErrors = [];
        return required;
     }
    trySubmit: boolean = false;
    async submit() {
        this.trySubmit = true;
        if (this.attachmentsRequired || this.invalidReportingPeriod || this.invalidYear) return;

        try {
            const item: VoyageDataSubmissionModel = {
                voyageDataSubmissionId: 0,
                financialYearReportingPeriodId: this.financialYearReportingPeriodId,
                ballastLegsIncluded: this.ballastLegsIncluded,
                year: this.yearAsDate.getFullYear(),
                financialYear: this.selectedFinancialYear,
                identifier: Guid.generateGuid()
            };

            const response = await this.voyageDataSubmissionService.saveVoyageDataSubmission(item, [], this.attachments, this.culture.code, this.customerId, this.selectedBusinessUnitId);
            this.validationErrors = response.validationErrors;
            if (response.validationErrors.length == 0) {
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                } else if (response && response.generatedId > 0) {
                    const voyageDataSubmissionId = response.generatedId;

                    this.dialogClientService.hide();
                    this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VoyageDataSubmission, { id: voyageDataSubmissionId });
                }
            }
        }
        catch (e) {
            const content = e._content;
            if (content && content.validationResult && content.validationResult.hasError) {
                this.validationsClientService.showValidationErrors(content.validationResult);
            }
            else {
                this.notificationService.show("Error submitting Voyage Data Submission", "", NotificationType.Error);
            }
        }
    }

    convertFinancialYearToDate() {
        const date = new Date();
        let financialYear = this.selectedFinancialYear;

        if (!financialYear)
            return;

        if (!this.selectedFinancialYearReportingPeriod || !this.selectedFinancialYearReportingPeriod.additionalData || this.financialYearTypeId == FinancialYearTypeEnum.January) {
            date.setFullYear(financialYear);
            this.yearAsDate = date;
            return;
        }

        switch(this.financialYearTypeId)
        {
            case FinancialYearTypeEnum.April:
                if (this.selectedFinancialYearReportingPeriod && this.selectedFinancialYearReportingPeriod.additionalData.monthNumber >= 4) {
                    financialYear--;
                }
                break;
            case FinancialYearTypeEnum.July:
                if (this.selectedFinancialYearReportingPeriod && this.selectedFinancialYearReportingPeriod.additionalData.monthNumber >= 7) {
                    financialYear--;
                }
                break;
            case FinancialYearTypeEnum.October:
                if (this.selectedFinancialYearReportingPeriod && this.selectedFinancialYearReportingPeriod.additionalData.monthNumber >= 10) {
                    financialYear--;
                }
                break;
        }

        date.setFullYear(financialYear);
        this.yearAsDate = date;
    }
}